import React, { useState, useEffect, useCallback } from 'react';
import API from '../lib/api';
import { Clock, Download, Search, Filter, MapPin, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';

export default function TimeRecordsPage() {
  const [records, setRecords] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1); // First day of current month
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  const fetchWorkers = useCallback(async () => {
    try {
      const response = await API.get('/workers');
      setWorkers(response.data);
    } catch (err) {
      console.error('Error fetching workers:', err);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/time-records?start_date=${startDate}&end_date=${endDate}`;
      if (selectedWorker !== 'all') {
        url += `&worker_id=${selectedWorker}`;
      }
      const response = await API.get(url);
      setRecords(response.data);
    } catch (err) {
      console.error('Error fetching records:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedWorker]);

  useEffect(() => {
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const handleExport = async (format) => {
    try {
      let url = `/time-records/export?start_date=${startDate}&end_date=${endDate}&format=${format}`;
      if (selectedWorker !== 'all') {
        url += `&worker_id=${selectedWorker}`;
      }
      
      const response = await API.get(url, { responseType: 'blob' });
      
      // Determine file extension and MIME type
      const extensions = { csv: 'csv', excel: 'xlsx', pdf: 'pdf' };
      const mimeTypes = { 
        csv: 'text/csv', 
        excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 
        pdf: 'application/pdf' 
      };
      
      const blob = new Blob([response.data], { type: mimeTypes[format] });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `registro_horario_${startDate}_${endDate}.${extensions[format]}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      setExportDialogOpen(false);
    } catch (err) {
      console.error('Error exporting records:', err);
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const showLocation = (location, type) => {
    if (location) {
      setSelectedLocation({ ...location, type });
      setLocationDialogOpen(true);
    }
  };

  // Calculate totals
  const totalHours = records.reduce((sum, r) => sum + (r.total_hours || 0), 0);
  const totalRecords = records.length;

  return (
    <div className="space-y-6" data-testid="time-records-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900" >
            Control Horario
          </h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Registro de fichajes de trabajadores</p>
        </div>
        
        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-lg shadow-md shadow-orange-200" data-testid="export-btn">
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Exportar Registros</DialogTitle>
              <DialogDescription>
                Exporta los registros de fichaje para cumplimiento legal (Art. 34.9 ET).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Se exportarán los registros del período <strong>{startDate}</strong> al <strong>{endDate}</strong>
                {selectedWorker !== 'all' && ` para el trabajador seleccionado`}.
              </p>
              <p className="text-sm text-gray-500">
                Total de registros: {totalRecords} | Total de horas: {totalHours.toFixed(2)}h
              </p>
              
              <div className="pt-2 space-y-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-gray-500">Formato de exportación</p>
                <div className="grid grid-cols-3 gap-2">
                  <Button 
                    onClick={() => handleExport('excel')} 
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 shadow-md shadow-orange-200 text-white"
                    data-testid="export-excel-btn"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM9.5 11.5l2 3.5-2 3.5h1.5l1.25-2.5 1.25 2.5H15l-2-3.5 2-3.5h-1.5l-1.25 2.5-1.25-2.5H9.5z"/>
                    </svg>
                    Excel
                  </Button>
                  <Button 
                    onClick={() => handleExport('pdf')} 
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="export-pdf-btn"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM8.5 13h1.25v4H8.5v-4zm3 0h1.75c.69 0 1.25.56 1.25 1.25v1.5c0 .69-.56 1.25-1.25 1.25H11.5v-4zm4.5 0h2v1h-1v.5h1v1h-1V17h-1v-4zm-3.5 1v2h.5v-2h-.5z"/>
                    </svg>
                    PDF
                  </Button>
                  <Button 
                    onClick={() => handleExport('csv')} 
                    variant="outline"
                    data-testid="export-csv-btn"
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 2l5 5h-5V4zM7 17v-4h1v4H7zm3-4h1c.55 0 1 .45 1 1v2c0 .55-.45 1-1 1h-1v-4zm4 0h1.5v1H14v.5h1v1h-1v.5h1.5v1H14c-.55 0-1-.45-1-1v-2c0-.55.45-1 1-1z"/>
                    </svg>
                    CSV
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setExportDialogOpen(false)}>Cerrar</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white border border-orange-100 rounded-lg p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <h3 className="font-semibold text-gray-900">Filtros</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Fecha inicio</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-10"
              data-testid="start-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Fecha fin</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-10"
              data-testid="end-date-input"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold text-gray-600">Trabajador</Label>
            <Select value={selectedWorker} onValueChange={setSelectedWorker}>
              <SelectTrigger data-testid="worker-filter">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los trabajadores</SelectItem>
                {workers.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={fetchRecords} variant="outline" className="w-full h-10">
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-orange-100 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Registros</p>
          <p className="text-2xl font-bold text-gray-900" >
            {totalRecords}
          </p>
        </div>
        <div className="bg-white border border-orange-100 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Horas Total</p>
          <p className="text-2xl font-bold text-gray-900" >
            {totalHours.toFixed(1)}h
          </p>
        </div>
        <div className="bg-white border border-orange-100 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Media/Día</p>
          <p className="text-2xl font-bold text-gray-900" >
            {totalRecords > 0 ? (totalHours / totalRecords * 1).toFixed(1) : 0}h
          </p>
        </div>
        <div className="bg-white border border-orange-100 rounded-lg p-4">
          <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold">En Curso</p>
          <p className="text-2xl font-bold text-amber-600" >
            {records.filter(r => !r.clock_out_time).length}
          </p>
        </div>
      </div>

      {/* Records Table */}
      <div className="bg-white border border-orange-100 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-orange-100 bg-[#FFF8F4]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Trabajador</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Fecha</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Entrada</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 hidden md:table-cell">Ubicación</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500">Salida</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 hidden md:table-cell">Ubicación</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-gray-500 text-right">Horas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Cargando...
                  </TableCell>
                </TableRow>
              ) : records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No hay registros en el período seleccionado
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id} className="hover:bg-orange-50/50" data-testid={`record-row-${record.id}`}>
                    <TableCell className="font-medium text-gray-900">{record.user_name}</TableCell>
                    <TableCell className="text-gray-600">{formatDate(record.clock_in_time)}</TableCell>
                    <TableCell className="text-gray-600 font-mono">{formatTime(record.clock_in_time)}</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {record.clock_in_location && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 p-1 h-auto"
                          onClick={() => showLocation(record.clock_in_location, 'Entrada')}
                        >
                          <MapPin className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-gray-600 font-mono">
                      {record.clock_out_time ? formatTime(record.clock_out_time) : (
                        <Badge className="bg-amber-100 text-amber-800 text-xs">En curso</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {record.clock_out_location && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-600 hover:text-blue-700 p-1 h-auto"
                          onClick={() => showLocation(record.clock_out_location, 'Salida')}
                        >
                          <MapPin className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold text-gray-900">
                      {record.total_hours ? `${record.total_hours}h` : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Location Dialog */}
      <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" />
              Ubicación de {selectedLocation?.type}
            </DialogTitle>
          </DialogHeader>
          {selectedLocation && (
            <div className="space-y-4">
              {selectedLocation.address && (
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Dirección</Label>
                  <p className="text-gray-900 mt-1">{selectedLocation.address}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Latitud</Label>
                  <p className="font-mono text-gray-900 mt-1">{selectedLocation.latitude?.toFixed(6)}</p>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500">Longitud</Label>
                  <p className="font-mono text-gray-900 mt-1">{selectedLocation.longitude?.toFixed(6)}</p>
                </div>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => window.open(`https://www.google.com/maps?q=${selectedLocation.latitude},${selectedLocation.longitude}`, '_blank')}
              >
                <MapPin className="w-4 h-4 mr-2" />
                Ver en Google Maps
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
